import { useEffect, type HTMLAttributes, type ReactNode } from 'react';
import { Heading, type HeadingProps } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import './PageHeader.css';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Page title — rendered as the page's single `<h1>`. */
  title: ReactNode;
  /** Optional one-line summary shown under the title (muted). */
  description?: ReactNode;
  /** Optional action slot (right-aligned; wraps below the title on narrow screens). Put the page's one primary Button here. */
  actions?: ReactNode;
  /** Optional back-link slot rendered above the title (e.g. a ghost Button or router Link). */
  back?: ReactNode;
  /** Optional extra content under the description (badges, meta line, tabs…). */
  children?: ReactNode;
  /**
   * Keep the `<h1>` for screen readers and the document outline but hide the block visually —
   * for pages whose first visible element already names the page (e.g. the dashboard hero).
   */
  visuallyHidden?: boolean;
  /** Extra props for the `<h1>` (id, data-testid, className…). */
  headingProps?: Omit<HeadingProps, 'level' | 'children'>;
  className?: string;
}

const APP_NAME = 'ReefGuide';

/**
 * PageHeader — the one page title block of every AppLayout page. Also sets the
 * document title ("<title> · ReefGuide") while the page is shown.
 * Owns the page's only `<h1>`; sections below use `<Section>` (h2).
 */
export function PageHeader({
  title,
  description,
  actions,
  back,
  children,
  headingProps,
  visuallyHidden = false,
  className = '',
  ...rest
}: PageHeaderProps) {
  const classes = ['rg-page-header', visuallyHidden ? 'rg-page-header--visually-hidden' : '', className];

  // The browser tab, history and screen-reader page announcement name the page, not only
  // "ReefGuide" (usability audit §4.5). Only plain-text titles are used.
  const documentTitle = typeof title === 'string' && title.trim() !== '' ? `${title} · ${APP_NAME}` : null;
  useEffect(() => {
    if (documentTitle === null) return undefined;
    globalThis.document.title = documentTitle;
    return () => {
      globalThis.document.title = APP_NAME;
    };
  }, [documentTitle]);
  return (
    <div className={classes.filter(Boolean).join(' ')} {...rest}>
      {back != null && <div className="rg-page-header__back">{back}</div>}
      <div className="rg-page-header__row">
        <div className="rg-page-header__titles">
          <Heading
            level={1}
            {...headingProps}
            className={['rg-page-header__title', headingProps?.className ?? ''].filter(Boolean).join(' ')}
          >
            {title}
          </Heading>
          {description != null && description !== '' && (
            <Text color="muted" className="rg-page-header__description">
              {description}
            </Text>
          )}
          {children != null && <div className="rg-page-header__meta">{children}</div>}
        </div>
        {actions != null && <div className="rg-page-header__actions">{actions}</div>}
      </div>
    </div>
  );
}

export default PageHeader;
